import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
/** Hooks */
import useAuth from 'hooks/useAuth';
import useFilters from 'hooks/useFilters';
import { useScrollToRef } from 'hooks/useScrollToRef';

/** Components */
import Box from '@mui/material/Box';
import LoadMore from 'components/LoadMore';
import LegacyBrandCard from 'components/Brand/LegacyBrandCard';
import NoProductsMatchMessage from 'components/Listing/Messages/NoProductsMatch';
import AdvertisingSelector from 'components/Listing/Cards/Advertising/AdvertisingSelector';

/** Block */
import ListingPage from 'blocks/ListingPage';

// Utils & Consts
import { log, noOp } from 'utils/functions';
import { DEFAULT_BRANDS_LIMIT } from 'constants/listing-defaults';
import { leadTimesList, minOrderList } from 'constants/category-filters';

/** API */
import { getBrandsByCategory } from 'api/brand';
import RecentBrowsedProductsCarousel from 'components/Carousels/common/RecentBrowsed/RecentBrowsedProductsCarousel';
import RecentBrowsedBrandsCarousel from 'components/Carousels/common/RecentBrowsed/RecentBrowsedBrandsCarousel';

const categoryBrandTabContentDefaultPropSx = {};

const categoryBrandTabContentDefaultPropInitialBrandsData = {
  totalBrands: 0,
  brands: [],
};

const CategoryBrandTabContent = ({
  slug,
  sx = categoryBrandTabContentDefaultPropSx,
  itemId = '',
  handleLimitChange = noOp,
  handleItemIdChange = noOp,
  initialBrandsData = categoryBrandTabContentDefaultPropInitialBrandsData,
}) => {
  const { user } = useAuth();
  const [offset, setOffset] = useState(0);
  const [lastFetch, setLastFetch] = useState('');
  const [total, setTotal] = useState(initialBrandsData?.totalBrands || 0);
  const [brands, setBrands] = useState(initialBrandsData?.brands || []);

  const [chunk, setChunk] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [previousQueryParams, setPreviousQueryParams] = useState({});

  const [, setShouldScrollTo] = useScrollToRef();

  const {
    onOffer,
    selectedFlairs,
    selectedSortBy,
    selectedMinOrder,
    showLoadingState,
    selectedLeadTime,
    selectedCountries,
    selectedBrandValues,
    setShowLoadingState,
  } = useFilters();

  const shippingCountryCode = user?.shippingCountry;

  useEffect(() => {
    setTotal(initialBrandsData?.totalBrands || 0);
    setBrands(initialBrandsData?.brands || []);
  }, [initialBrandsData]);

  useLayoutEffect(() => {
    setShouldScrollTo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryParams = useMemo(
    () => ({
      onOffer,
      offset,
      shippingCountryCode,
      sortBy: selectedSortBy,
      limit: DEFAULT_BRANDS_LIMIT,
      brandValues: selectedBrandValues.join(','),
      countries: selectedCountries.join(',').toUpperCase(),
      ...(selectedFlairs?.length && { flairs: selectedFlairs }),
      minOrder: minOrderList.find((o) => o.text === selectedMinOrder)?.value,
      leadTimeMaxDays: leadTimesList.filter(({ text }) => text === selectedLeadTime)[0]?.maximumDays,
    }),
    [
      onOffer,
      offset,
      selectedSortBy,
      selectedFlairs,
      selectedMinOrder,
      selectedLeadTime,
      selectedCountries,
      shippingCountryCode,
      selectedBrandValues,
    ]
  );

  // load more
  useEffect(() => {
    (async () => {
      const isQueryParamsChanged = JSON.stringify(previousQueryParams) !== JSON.stringify(queryParams);

      if (isQueryParamsChanged && offset > 0 && !isLoadingMore) {
        setIsLoadingMore(true);
        try {
          const data = await getBrandsByCategory(slug, queryParams);
          setChunk(data);
          setPreviousQueryParams(queryParams);
        } catch (error) {
          log.error(`couldnt fetch more brands. category-slug: ${slug}, params:${JSON.stringify(queryParams)}`);
        } finally {
          setIsLoadingMore(false);
        }
      }
    })();
  }, [queryParams]);

  const loadMoreBrands = () => {
    handleLimitChange();
    setOffset(brands.length);
  };

  useEffect(() => {
    if (isEmpty(chunk?.brands)) return;
    if (chunk?.brands?.length > 0) {
      setTotal(chunk.totalBrands);
      setBrands((prev) => [...prev, ...chunk.brands]);
      if (shippingCountryCode) {
        setLastFetch('user');
      } else {
        setLastFetch('guest');
      }
    }
  }, [chunk, shippingCountryCode]);

  // when changing the shippingCountryCode, reset the brands
  useEffect(() => {
    if ((shippingCountryCode && lastFetch === 'guest') || (!shippingCountryCode && lastFetch === 'user')) {
      setTotal(0);
      setBrands([]);
    }
  }, [shippingCountryCode, lastFetch]);

  useEffect(() => {
    if (showLoadingState) {
      setBrands([]);
      setTotal(0);
      setOffset(0);
    }
  }, [showLoadingState, slug]);

  // When changing the initial products and brands value, set loading state to false
  useEffect(() => {
    setShowLoadingState(false);
  }, [initialBrandsData, setShowLoadingState]);

  return (
    <ListingPage sx={{ ...sx }}>
      <ListingPage.ItemContainer>
        {!showLoadingState &&
          (brands.length > 0 ? (
            brands.map((brand, index) => (
              <Fragment key={`brand-list-item-${brand.id}`}>
                <ListingPage.Item
                  id={brand.id}
                  key={`brand-id-${brand.id}`}
                  data-scroll-to-element={brand.id === itemId ? 'scroll-reference-id' : null}
                >
                  <LegacyBrandCard handleItemIdChange={handleItemIdChange} brand={brand} index={index} />
                </ListingPage.Item>
                <AdvertisingSelector index={index} listLength={brands.length} />
              </Fragment>
            ))
          ) : (
            <NoProductsMatchMessage />
          ))}
        {(isLoadingMore || showLoadingState) &&
          [1, 2, 3, 4, 5, 6, 7, 8].map((id) => (
            <ListingPage.Item key={`loading-brand-slug-${id}`}>
              <LegacyBrandCard isLoading />
            </ListingPage.Item>
          ))}
      </ListingPage.ItemContainer>
      <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
        <LoadMore
          total={total}
          viewed={brands.length}
          resource="brands"
          isLoading={isLoadingMore}
          onClickHandler={loadMoreBrands}
          progress={(brands.length / total) * 100}
        />
      </Box>
      <RecentBrowsedBrandsCarousel
        viewAllRef="plp"
        type="brands"
        sx={{ mt: { xs: 1, md: 3 } }}
        isParentLoading={isLoadingMore}
      />
      <RecentBrowsedProductsCarousel
        viewAllRef="plp"
        type="brands"
        sx={{ mt: { xs: 1, md: 3 } }}
        isParentLoading={isLoadingMore}
      />
    </ListingPage>
  );
};

CategoryBrandTabContent.propTypes = {
  sx: PropTypes.shape({}),
  slug: PropTypes.string.isRequired,
  initialBrandsData: PropTypes.shape({
    totalBrands: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  itemId: PropTypes.string,
  handleItemIdChange: PropTypes.func,
  handleLimitChange: PropTypes.func,
};

export default CategoryBrandTabContent;
